import { ChevronV2 } from 'assets/icons/ChevronV2'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { FieldWrapper, Input, InputWrapper, InvalidText, Label } from '../Form'
import {
  ChevronIconContainer,
  MultiSelectFieldWrapper,
  MultiSelectInput,
  Pill,
  Remove,
  SelectOption,
  SelectOptionsContainer,
} from './Select.styles'

export type Option = { label: string; value: string | number | null }

export const Multiselect = ({
  options,
  onChange,
  width,
  placeholder,
  values,
  label,
  error,
}: {
  options: Option[]
  onChange: (values: Option[] | null) => void
  width?: string
  placeholder?: string
  values?: Option[] | null
  label?: string
  error?: string
}) => {
  const [inputValue, setInputValue] = useState('')
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setInputValue('')
        inputRef?.current?.blur()
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [])

  const calculatePosition = () => {
    const rect = inputRef.current?.getBoundingClientRect()
    const windowHeight = window.innerHeight
    const windowWidth = window.innerWidth
    const containerHeight = 200
    const containerWidth = 200
    if (!rect)
      return {
        top: 0,
        left: 0,
      }
    const position = {
      top: rect.top + 50,
      left: rect.left,
    }

    if (position.top + containerHeight > windowHeight) {
      return {
        bottom: windowHeight - rect.top + 10,
        left: rect.left,
      }
    }
    if (position.left + containerWidth > windowWidth) {
      position.left = rect.left - containerWidth
    }
    return {
      top: position.top,
      left: position.left,
    }
  }

  useEffect(() => {
    if (!open) {
      setInputValue('')
    }
  }, [open])

  useEffect(() => {
    inputRef.current?.addEventListener('focus', () => {
      setOpen(true)
    })
    inputRef.current?.addEventListener('blur', () => {
      setTimeout(() => {
        setOpen(false)
      }, 100)
    })
  }, [])

  const handleRemove = (option: Option) => {
    onChange(values?.filter((v) => v?.value !== option?.value) || null)
  }

  return (
    <InputWrapper width={width}>
      {label && <Label>{label}</Label>}

      <FieldWrapper>
        <MultiSelectFieldWrapper onClick={() => inputRef.current?.focus()} invalid={!!error}>
          {values?.map((v) => (
            <Pill key={v.value}>
              {v.label}
              <Remove
                onClick={(e) => {
                  e.stopPropagation()
                  handleRemove(v)
                }}
              >
                ×
              </Remove>
            </Pill>
          ))}

          <MultiSelectInput
            ref={inputRef}
            value={inputValue}
            placeholder={values?.length ? '' : placeholder}
            onChange={(e) => setInputValue(e.target.value)}
            onFocus={() => setOpen(true)}
          />

          {error && <InvalidText>{error}</InvalidText>}
          <ChevronIconContainer>
            <ChevronV2 open={open} />
          </ChevronIconContainer>
        </MultiSelectFieldWrapper>
      </FieldWrapper>
      {open &&
        createPortal(
          <SelectOptionsContainer
            style={{
              ...calculatePosition(),
            }}
            ref={dropdownRef}
          >
            {options.map((option) => (
              <SelectOption
                key={option.value}
                active={values?.some((value) => value.value === option.value)}
                onMouseDown={() => {
                  if (values?.some((v) => v?.value === option.value)) {
                    handleRemove(option)
                    return
                  }
                  onChange([...(values || []), option])
                  setInputValue('')
                  setOpen(false)
                  inputRef?.current?.blur()
                }}
              >
                {option.label}
              </SelectOption>
            ))}
          </SelectOptionsContainer>,
          document.body,
        )}
    </InputWrapper>
  )
}
